import { supabase } from '@/shared/lib/supabase';
import { configuracoesService } from './configuracoesService';
import type { IdentidadeSonoraConfig, IdentidadeSonoraClipe } from '@/shared/models/identidadeSonora.model';

export const identidadeSonoraService = {
  async buscarParaTela(
    telaId: string,
  ): Promise<{ config: IdentidadeSonoraConfig | null; clipes: IdentidadeSonoraClipe[] }> {
    const { data, error } = await supabase.rpc(
      'player_buscar_identidade_sonora' as never,
      { p_tela_id: telaId } as never,
    );
    if (error) throw error;
    const r = data as { config?: IdentidadeSonoraConfig | null; clipes?: IdentidadeSonoraClipe[] } | null;
    return { config: r?.config ?? null, clipes: r?.clipes ?? [] };
  },

  async buscarConfig(empresaId: string): Promise<IdentidadeSonoraConfig | null> {
    const { data, error } = await supabase
      .from('identidade_sonora_config')
      .select('*')
      .eq('empresa_id', empresaId)
      .maybeSingle();
    if (error) throw error;
    return (data as IdentidadeSonoraConfig | null) ?? null;
  },

  async salvarConfig(empresaId: string, ativo: boolean, intervaloFaixas: number): Promise<void> {
    const { error } = await supabase
      .from('identidade_sonora_config')
      .upsert({ empresa_id: empresaId, ativo, intervalo_faixas: intervaloFaixas } as never, { onConflict: 'empresa_id' });
    if (error) throw error;
  },

  async listarClipes(empresaId: string): Promise<IdentidadeSonoraClipe[]> {
    const { data, error } = await supabase
      .from('identidade_sonora_clipes')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('ordem', { ascending: true });
    if (error) throw error;
    return (data as IdentidadeSonoraClipe[] | null) ?? [];
  },

  async listarClipesAtivos(empresaId: string): Promise<IdentidadeSonoraClipe[]> {
    const { data, error } = await supabase
      .from('identidade_sonora_clipes')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('ativo', true)
      .order('ordem', { ascending: true });
    if (error) throw error;
    return (data as IdentidadeSonoraClipe[] | null) ?? [];
  },

  async criarClipe(
    empresaId: string, url: string, titulo: string | null, ganho: number | null,
  ): Promise<IdentidadeSonoraClipe> {
    const { data, error } = await supabase
      .from('identidade_sonora_clipes')
      .insert({ empresa_id: empresaId, url, titulo, ativo: true, ordem: 0, ganho } as never)
      .select('*')
      .single();
    if (error) throw error;
    return data as IdentidadeSonoraClipe;
  },

  /** Preenche o nivelamento de uma narração enviada antes da medição existir. */
  async atualizarGanhoClipe(empresaId: string, clipeId: string, ganho: number): Promise<void> {
    const { error } = await supabase
      .from('identidade_sonora_clipes')
      .update({ ganho } as never)
      .eq('empresa_id', empresaId)
      .eq('id', clipeId);
    if (error) throw error;
  },

  async atualizarAtivoClipe(empresaId: string, clipeId: string, ativo: boolean): Promise<void> {
    const { error } = await supabase
      .from('identidade_sonora_clipes')
      .update({ ativo } as never)
      .eq('empresa_id', empresaId)
      .eq('id', clipeId);
    if (error) throw error;
  },

  async atualizarTituloClipe(empresaId: string, clipeId: string, titulo: string): Promise<void> {
    const { error } = await supabase
      .from('identidade_sonora_clipes')
      .update({ titulo } as never)
      .eq('empresa_id', empresaId)
      .eq('id', clipeId);
    if (error) throw error;
  },

  async removerClipe(empresaId: string, clipeId: string): Promise<void> {
    const BUCKET = 'midias-audio';
    const { data: row, error: selectError } = await supabase
      .from('identidade_sonora_clipes')
      .select('url')
      .eq('empresa_id', empresaId)
      .eq('id', clipeId)
      .maybeSingle();
    if (selectError) throw selectError;

    const storageUrl = (row as { url?: string } | null)?.url;
    if (storageUrl) {
      const path = storageUrl.split(`/${BUCKET}/`)[1];
      if (path) {
        const { error: removeError } = await supabase.storage.from(BUCKET).remove([path]);
        if (removeError) throw removeError;
      }
    }

    const { error } = await supabase
      .from('identidade_sonora_clipes')
      .delete()
      .eq('empresa_id', empresaId)
      .eq('id', clipeId);
    if (error) throw error;
  },

  /**
   * Faz upload de um clipe para o bucket `midias-audio/{empresaId}/`.
   * Reaproveita o mesmo upload compartilhado do Momento Especial — mesmo
   * bucket e convenção de path.
   */
  uploadClipe(
    empresaId:  string,
    file:       File,
    onProgress: (pct: number) => void,
  ): Promise<string> {
    return configuracoesService.uploadAudioBucket(empresaId, file, onProgress);
  },
};
